import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Cpu,
  FileText,
  Wrench,
  MessageSquare,
  BookOpen,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  AlertTriangle,
  Factory,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { useAuth } from "../hooks/useAuth";
import { useSocketStore } from "../store/socketStore";
import clsx from "clsx";
import { ROLES } from "../constants/roles";
import AppSwitcher from "./AppSwitcher";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/machines", icon: Cpu, label: "Machines" },
  { path: "/plants", icon: Factory, label: "Plants" },
  { path: "/sops", icon: FileText, label: "SOPs" },
  { path: "/maintenance", icon: Wrench, label: "Maintenance" },
  { path: "/assistant", icon: MessageSquare, label: "AI Assistant" },
  {
    path: "/knowledge",
    icon: BookOpen,
    label: "Knowledge Base",
    allowedRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TECHNICIAN],
  },
  {
    path: "/errors",
    icon: AlertTriangle,
    label: "Reports",
    allowedRoles: [
      ROLES.SUPER_ADMIN,
      ROLES.PLANT_MANAGER,
      ROLES.MACHINE_OPERATOR,
      ROLES.MAINTENANCE_TECHNICIAN,
    ],
  },
];

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebarOpen");
    return saved !== null ? saved === "true" : true;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { logout } = useAuthStore();
  const { user } = useAuth();
  const { connect, disconnect } = useSocketStore();
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = user?.role?.toLowerCase();
  const mainRef = useRef(null);

  // Ensure the page always starts from the top when opening/navigating routes
  useEffect(() => {
    const scrollToTop = () => {
      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    };

    scrollToTop();

    const rafId = requestAnimationFrame(scrollToTop);
    const timeoutId = setTimeout(scrollToTop, 60);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarOpen", String(next));
      return next;
    });
  };

  useEffect(() => {
    // Continuously notify Recharts and responsive elements as sidebar width animates
    const interval = setInterval(() => {
      window.dispatchEvent(new Event("resize"));
    }, 40);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      window.dispatchEvent(new Event("resize"));
    }, 350);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    const path = location.pathname.split("/")[1] || "dashboard";
    const matched = navItems.find((item) => item.path === `/${path}`);

    let title = "ConvoLink";
    if (matched) title = matched.label;
    else if (path && path !== "")
      title = path.charAt(0).toUpperCase() + path.slice(1);

    document.title = `${title} - ConvoLink`;
  }, [location.pathname]);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (!showLogoutConfirm) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isLoggingOut) {
        setShowLogoutConfirm(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showLogoutConfirm, isLoggingOut]);

  const handleLogoutClick = () => {
    setMobileMenuOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  const filteredNavItems = navItems.filter((item) => {
    if (!item.allowedRoles) return true;
    return item.allowedRoles.includes(userRole);
  });

  const NavItem = ({ item, isMobile = false }) => {
    const Component = NavLink;

    const handleClick = () => {
      if (mainRef.current) {
        mainRef.current.scrollTop = 0;
      }
      window.scrollTo(0, 0);
      if (isMobile) {
        setMobileMenuOpen(false);
      }
    };

    return (
      <Component
        to={item.path}
        onClick={handleClick}
        className={({ isActive }) =>
          clsx(
            "flex items-center gap-3 rounded-lg p-3 text-sm transition-colors",
            !sidebarOpen && !isMobile && "justify-center",
            isActive
              ? "bg-blue-500/10 text-blue-400 font-semibold"
              : "text-slate-400 hover:bg-slate-800 hover:text-white",
          )
        }
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        {(sidebarOpen || isMobile) && <span>{item.label}</span>}
      </Component>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-white text-slate-800">
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: "0%" }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
              className="fixed inset-y-0 left-0 w-72 bg-slate-950 border-r border-slate-800 flex flex-col z-50"
            >
              <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800 flex-shrink-0">
                <img
                  src="/Machine-Interface-light.png"
                  alt="Logo"
                  className="h-12 w-auto object-contain"
                />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 text-slate-400 rounded-full hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {filteredNavItems.map((item) => (
                  <NavItem key={item.path} item={item} isMobile />
                ))}
              </nav>
              <div className="p-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={handleLogoutClick}
                  className="flex w-full items-center gap-3 rounded-lg p-3 text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
                <div className="mt-4 flex items-center justify-start gap-2 pl-3">
                  <span className="text-xs text-slate-400">Powered by</span>
                  <img
                    src="/convolink-light.png"
                    alt="ConvoLink"
                    className="h-6 w-auto object-contain"
                  />
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <aside
        className={clsx(
          "hidden lg:flex flex-col bg-slate-950 border-r border-slate-800 transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20",
        )}
      >
        <div
          className={clsx(
            "h-16 flex items-center border-b border-slate-800 px-4",
            sidebarOpen ? "justify-start" : "justify-center",
          )}
        >
          {sidebarOpen ? (
            <img
              src="/Machine-Interface-light.png"
              alt="Logo"
              className="h-12 w-auto object-contain"
            />
          ) : (
            <img
              src="/Machine-Interface-logo.png"
              alt="Logo Icon"
              className="h-8 w-auto"
            />
          )}
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {filteredNavItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button
            type="button"
            onClick={handleLogoutClick}
            className={clsx(
              "flex w-full items-center gap-3 rounded-lg p-3 text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400",
              !sidebarOpen && "justify-center",
            )}
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
          {sidebarOpen && (
            <div className="mt-4 flex items-center justify-start gap-2 pl-3">
              <span className="text-xs text-slate-400">Powered by</span>
              <img
                src="/convolink-light.png"
                alt="ConvoLink"
                className="h-6 w-auto object-contain"
              />
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-screen">
        <header className="h-16 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-slate-400 rounded-full hover:bg-slate-800 lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={toggleSidebar}
              className="hidden lg:inline-flex p-2 text-slate-400 rounded-full hover:bg-slate-800 transition-colors"
              title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              <ChevronLeft
                className={clsx(
                  "w-5 h-5 transition-transform duration-300",
                  !sidebarOpen && "rotate-180",
                )}
              />
            </button>
          </div>
          <div className="flex items-center gap-4">
            <AppSwitcher />
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-100">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-slate-400 capitalize">
                {user?.role?.replace("_", " ")}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 font-bold text-sm border border-slate-600">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
          </div>
        </header>

        <main
          ref={mainRef}
          className={clsx(
            "flex-1 transition-all duration-300",
            location.pathname.startsWith("/assistant")
              ? "overflow-hidden p-0 flex flex-col min-h-0 bg-white"
              : "overflow-y-auto p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8 bg-slate-50"
          )}
        >
          <div
            className={clsx(
              "w-full mx-auto transition-all duration-300",
              location.pathname.startsWith("/assistant")
                ? "flex-1 flex flex-col h-full min-h-0 max-w-none px-0"
                : sidebarOpen
                ? "max-w-7xl 2xl:max-w-[1600px]"
                : "max-w-[1850px] w-full px-0 sm:px-2 lg:px-4"
            )}
          >
            <Outlet context={{ sidebarOpen, mainRef }} />
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {showLogoutConfirm && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
                onClick={() => {
                  if (!isLoggingOut) setShowLogoutConfirm(false);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-4 my-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center shrink-0">
                      <LogOut className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                        Confirm Logout
                      </h2>
                      <p className="text-xs text-slate-500 mt-0.5">
                        End your current active session
                      </p>
                    </div>
                  </div>

                  {/* Warning Description */}
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Are you sure you want to log out? Any unsaved changes in progress may be lost, and you will need to sign in again to access the machine interface.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-2.5 pt-2">
                    <button
                      type="button"
                      disabled={isLoggingOut}
                      onClick={() => setShowLogoutConfirm(false)}
                      className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={isLoggingOut}
                      onClick={handleConfirmLogout}
                      className="btn bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm py-2 px-4 shadow-md shadow-rose-500/20 inline-flex items-center gap-2 font-medium"
                    >
                      {isLoggingOut ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Logging out...</span>
                        </>
                      ) : (
                        <>
                          <LogOut className="w-4 h-4" />
                          <span>Log Out</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default Layout;